import { CSSProperties } from 'react';
import AssetIcon from '../../../components/AssetIcon';
import { Subscription } from '../../../types/subscription';
import {
  formatBalance,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '../../../helpers/NumberFormatter';
import { tokens } from '../Content/Calculations/tokens';

interface Props {
  subscription: Subscription;
  maxDecimals: number;
}

/**
 * New subscription masthead (design: subscription_redesigns/layout.jsx).
 * Hierarchy: identity row → Total Balance hero + stable/asset distribution bar →
 * 2×2 metric grid (Stable / Asset / Current Profit / Price Change). Spot price
 * anchors the identity row on the right.
 */
const colorFor = (value: number | undefined): string => {
  if (!value) return tokens.textPrimary;
  return value < 0 ? tokens.red : tokens.green;
};

const StatusPill = ({ isActive }: { isActive: boolean }) => (
  <span
    style={{
      padding: '2px 10px',
      fontSize: 10,
      letterSpacing: '0.2em',
      background: isActive ? 'rgba(0,176,97,0.12)' : 'rgba(240,22,47,0.12)',
      color: isActive ? tokens.green : tokens.red,
      border: `1px solid ${isActive ? tokens.green : tokens.red}`,
      borderRadius: 999,
      fontFamily: tokens.fontMono,
      fontWeight: 700,
      whiteSpace: 'nowrap',
    }}
  >
    {isActive ? '● ACTIVE' : '● PAUSED'}
  </span>
);

const BalanceDistribution = ({
  stableUsd,
  assetUsd,
}: {
  stableUsd: number;
  assetUsd: number;
}) => {
  const total = stableUsd + assetUsd;
  const stablePct = total > 0 ? (stableUsd / total) * 100 : 0;
  const assetPct = 100 - stablePct;
  const fmtPct = (n: number) => `${formatNumber(n, 2)}%`;

  return (
    <div style={{ marginTop: 14, width: '75%' }}>
      <div
        style={{
          display: 'flex',
          height: 10,
          width: '100%',
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 2,
          overflow: 'hidden',
          gap: 2,
        }}
      >
        <div
          title={`Stable ${fmtPct(stablePct)}`}
          style={{
            width: `${Math.max(stablePct, 0.8)}%`,
            background: tokens.green,
            borderRadius: 1,
          }}
        />
        <div
          title={`Asset ${fmtPct(assetPct)}`}
          style={{
            width: `${assetPct}%`,
            background: tokens.amber,
            borderRadius: 1,
          }}
        />
      </div>
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: tokens.green,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 10,
              fontFamily: tokens.fontMono,
              color: tokens.textMuted,
              letterSpacing: '0.18em',
            }}
          >
            STABLE
          </span>
          <span
            style={{
              fontSize: 14,
              fontFamily: tokens.fontDisplay,
              color: tokens.textSecondary,
              marginLeft: 6,
            }}
          >
            {fmtPct(stablePct)}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 14,
              fontFamily: tokens.fontDisplay,
              color: tokens.textSecondary,
              marginRight: 6,
            }}
          >
            {fmtPct(assetPct)}
          </span>
          <span
            style={{
              fontSize: 10,
              fontFamily: tokens.fontMono,
              color: tokens.textMuted,
              letterSpacing: '0.18em',
            }}
          >
            ASSET
          </span>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: tokens.amber,
              flexShrink: 0,
            }}
          />
        </div>
      </div>
    </div>
  );
};

const Header = ({ subscription, maxDecimals }: Props) => {
  const {
    baseAsset,
    quoteAsset,
    exchange,
    isActive,
    latestPrice,
    totalBalanceInStable,
    stableBalance,
    assetBalance,
    assetBalanceInStable,
    currentProfit,
    currentPriceChange,
  } = subscription;

  const metricCell: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
  };

  const metrics: Array<[string, string, string, string]> = [
    [
      '02',
      'STABLE BALANCE',
      formatBalance(stableBalance),
      tokens.textSecondary,
    ],
    [
      '03',
      'ASSET BALANCE',
      `${formatNumber(assetBalance, maxDecimals)} ${baseAsset}`,
      tokens.textSecondary,
    ],
    [
      '04',
      'CURRENT PROFIT',
      formatPercentage(currentProfit ?? 0, 2, false, 'always'),
      colorFor(currentProfit),
    ],
    [
      '05',
      'PRICE CHANGE',
      formatPercentage(currentPriceChange ?? 0, 2, false, 'always'),
      colorFor(currentPriceChange),
    ],
  ];

  return (
    <div
      style={{
        // Transparent so the content root's full-bleed profit wash shows through.
        background: 'transparent',
        padding: '28px 0',
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        width: '100%',
        maxWidth: 1400,
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      {/* identity row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          marginBottom: 24,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 88,
            height: 88,
            flexShrink: 0,
          }}
        >
          <AssetIcon baseAsset={baseAsset} width={'100%'} height={'100%'} />
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: isActive ? tokens.green : tokens.red,
              border: '2px solid #000',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span
            style={{
              fontSize: 'clamp(34px, 4vw, 44px)',
              fontFamily: tokens.fontDisplay,
              color: tokens.textPrimary,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {baseAsset}
            <span style={{ color: tokens.textFaint }}> / </span>
            {quoteAsset}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                fontSize: 13,
                color: tokens.textMuted,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.15em',
              }}
            >
              {exchange}
            </span>
            <StatusPill isActive={isActive} />
          </span>
        </div>
        {/* Spot price — smallest, anchors the right */}
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            fontFamily: tokens.fontMono,
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: tokens.textFaint,
              letterSpacing: '0.2em',
            }}
          >
            SPOT
          </span>
          <span
            style={{
              fontSize: 18,
              color: tokens.textPrimary,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            {formatPrice(latestPrice, true, 7)}
          </span>
          <span
            style={{
              fontSize: 13,
              color: colorFor(currentPriceChange),
              fontWeight: 700,
            }}
          >
            {formatPercentage(currentPriceChange ?? 0, 2, false, 'always')}
          </span>
        </span>
      </div>

      {/* Total Balance hero + 2×2 metric grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 6fr) minmax(0, 6fr)',
          gap: 56,
          alignItems: 'flex-end',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span
              style={{
                fontSize: 11,
                color: tokens.textFaint,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.1em',
              }}
            >
              01
            </span>
            <span
              style={{
                fontSize: 11,
                color: tokens.textMuted,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.2em',
              }}
            >
              TOTAL BALANCE
            </span>
          </div>
          <div
            style={{
              fontSize: 'clamp(48px, 6.5vw, 80px)',
              fontFamily: tokens.fontDisplay,
              color: tokens.textPrimary,
              lineHeight: 0.88,
              letterSpacing: '-0.03em',
              marginTop: 4,
              wordBreak: 'break-word',
            }}
          >
            {formatBalance(totalBalanceInStable)}
          </div>
          <BalanceDistribution
            stableUsd={stableBalance}
            assetUsd={assetBalanceInStable}
          />
        </div>

        <div
          style={{
            display: 'grid',
            // Content-sized columns pushed to the right so the two columns sit
            // together (close to each other) and align with the spot price.
            gridTemplateColumns: 'max-content max-content',
            justifyContent: 'flex-end',
            rowGap: 18,
          }}
        >
          {metrics.map(([num, label, value, color], index) => {
            const isLeft = index % 2 === 0;
            return (
              <div
                key={num}
                style={{
                  ...metricCell,
                  padding: isLeft ? '0 24px 0 0' : '0 0 0 24px',
                  borderRight: isLeft ? tokens.borderFaint : 'none',
                  textAlign: 'right',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'baseline',
                    justifyContent: 'flex-end',
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      color: tokens.textFaint,
                      fontFamily: tokens.fontMono,
                      letterSpacing: '0.1em',
                    }}
                  >
                    {num}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: tokens.textSecondary,
                      fontFamily: tokens.fontMono,
                      letterSpacing: '0.18em',
                    }}
                  >
                    {label}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 40,
                    fontFamily: tokens.fontDisplay,
                    color,
                    marginTop: 4,
                    lineHeight: 1,
                    letterSpacing: '-0.01em',
                    wordBreak: 'break-word',
                  }}
                >
                  {value}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Header;
